import React, { useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import ProtectedRoute, {
  isAuthoritiesInvalid,
  isTokenInvalidOrAbsent,
} from 'src/views/pages/login/ProtectedRoute'
import { InformationContextProvider } from '../context/InformationContext'
import { SetupContextProvider } from '../context/SetupContext'

// Pages

const MentorPublicPage = React.lazy(() =>
  import('../../mentor/components/MentorPublicPage/MentorPublicPage'),
)

const BusinessPublicPage = React.lazy(() =>
  import('../../business/components/BusinessPublicPage/BusinessPublicPage'),
)

const ClinicPromotionPublicPage = React.lazy(() =>
  import('../../clinic/components/ClinicPromotionPage/ClinicPromotionPublicPage'),
)

const EventBooking = React.lazy(() => import('../components/EventBooking'))
const PackBuying = React.lazy(() => import('../components/PackBuying'))
const EventListing = React.lazy(() => import('../components/EventListing'))
const LiveSessionPageWrapperForWebview = React.lazy(() => import('../components/LiveSessionPage/LiveSessionPageWrapperForWebview'))

const MentorLandingPage = React.lazy(() => import('../components/MentorLandingPage'))

const ClinicBrandLandingPage = React.lazy(() =>
  import('../../clinic/components/ClinicBrandPages/ClinicBrandLandingPage'),
)
const ClinicLandingPage = React.lazy(() =>
  import('../../clinic/components/ClinicBrandPages/ClinicLandingPage'),
)

const ClinicPublicBookingPageOfMentor = React.lazy(() =>
  import('../../clinic/components/ClinicBrandPages/ClinicPublicBookingPageOfMentor'),
)

const EventBookingWrapperForWebview = React.lazy(() =>
  import('../components/EventBooking/EventBookingWrapperForWebview'),
)
const LiveSessionScreen = React.lazy(() => import('../components/LiveSessionPage/index'))
// const HotlineChamberPage = React.lazy(() => import('../components/HotlineChamberPage'))

const AssessmentListingPage = React.lazy(() => import('../components/AssessmentsListingPage'))
const AssessmentPlayer = React.lazy(() => import('../components/AssessmentPlayer'))

const JournalsListingPage = React.lazy(() => import('../components/JournalsListingPage'))
const JournalLandingPage = React.lazy(() =>
  import('../components/JournalsListingPage/JournalLandingPage'),
)

const ArticlesListingPage = React.lazy(() => import('../components/ArticlesListingPage'))
const ArticlePage = React.lazy(() => import('../components/ArticlePage'))
const UserActivityListingPage = React.lazy(() =>
  import('../components/UserActivityListingPage/UserActivityListingPageWebWrapper'),
)

const TherapistPsychiatristListingPage = React.lazy(() =>
  import('../components/ServiceProvidersListingPage/TherapistPsychiatristListingPage'),
)

const MyDedicatedPrescriptionPage = React.lazy(() =>
  import('../components/MyDedicatedPrescriptionPage'),
)
const RxViewerPage = React.lazy(() => import('../components/RxViewerPage'))
const ReceiptViewerPage = React.lazy(() => import('../components/ReceiptViewerPage'))

const TechniqueListingPage = React.lazy(() => import('../components/TechniqueListingPage'))
const TechniquePage = React.lazy(() => import('../components/TechniqueListingPage/TechniquePage'))

const BreatheListingPage = React.lazy(() => import('../components/BreatheListingPage'))

const CourseCategoryListingPage = React.lazy(() =>
  import('../components/CourseCategoryListingPage/index'),
)

const CourseCategoryLandingPage = React.lazy(() =>
  import('../components/CourseCategoryListingPage/CourseCategoryLandingPage'),
)

const AppHomepage = React.lazy(() => import('../components/AppHomepage'))
const AumhumSite = React.lazy(() => import('../components/AumhumSite/AumhumSite'))
const JoinPage = React.lazy(() => import('../components/AumhumSite/JoinPage'))
const SelfCarePublicLanding = React.lazy(() =>
  import('../components/AppHomepage/PublicAppHomepage').then((m) => ({
    default: m.PublicAppHomepage,
  })),
)

// Root landing: new public marketing site for unauthenticated visitors; the
// authenticated tile experience stays at / via AppHomepage.
//
// Patient-Prospect override (docs/PATIENT_PROSPECT_SIGNUP_PLAN.md §5.3):
// when a logged-in user holds ROLE_PATIENT_PROSPECT and *no other* user-
// facing role (no ROLE_USER, no ROLE_PATIENT, etc.), the wellness tile
// experience is the wrong destination — they're here for healthcare, not
// for the meditation app. Force-redirect them to /care/status. Multi-role
// users (e.g. wellness ROLE_USER + prospect) keep landing on AppHomepage
// where a banner inside CareStatusView's logged-in flow surfaces the
// in-progress care request without overriding their primary persona.
function RootLanding() {
  const token = localStorage.getItem('token')
  const authoritiesRaw = localStorage.getItem('authorities')
  const isPublic =
    isTokenInvalidOrAbsent(token) || isAuthoritiesInvalid(authoritiesRaw)
  if (isPublic) return <AumhumSite view="home" />
  let parsed = []
  try {
    parsed = JSON.parse(authoritiesRaw) || []
  } catch {
    parsed = []
  }
  const auth = Array.isArray(parsed) ? parsed : []
  const isProspect = auth.includes('ROLE_PATIENT_PROSPECT')
  const PROSPECT_OVERRIDE_BLOCKERS = ['ROLE_USER', 'ROLE_PATIENT', 'ROLE_PROFESSIONAL_USER']
  const hasBlocker = auth.some((r) => PROSPECT_OVERRIDE_BLOCKERS.includes(r))
  if (isProspect && !hasBlocker) {
    return <Navigate to="/care/status" replace />
  }
  return <AppHomepage />
}
const ActivityPlanPage = React.lazy(() =>
  import('../components/ActivityPlansPage/ActivityPlanWebWrapper.jsx'),
)
const ActivityPlanListingPage = React.lazy(() =>
  import('../components/ActivityPlanPublicListing/ActivityPlanPublicListing'),
)

const BehavioralAnxietyPlan = React.lazy(() =>
  import('../components/PersonalizedPlan/BehavioralAddictionPlan'),
)

const AddictionTypesSelectionPage = React.lazy(() =>
  import('../components/AddictionTypesSelectionPage/AddictionTypesSelection'),
)

const WebinarListingPage = React.lazy(() => import('../../business/views/Webinars/WebinarsListing'))
const TimerPage = React.lazy(() => import('../../consumer/components/TimersPage'))

const MentorBookingRequestActivePage = React.lazy(() =>
  import('../../mentor/components/MentorBookingRequestPage/MentorBookingRequestActivePage'),
)
const MentorBookingRequestAllPage = React.lazy(() =>
  import('../../mentor/components/MentorBookingRequestPage'),
)

export default function ConsumerLayout() {
  useEffect(() => {
    if (window.location.pathname !== '/') {
      localStorage.setItem('lastChosenFlow', 'consumer')
      sessionStorage.setItem('chosenSomeFlowInThisSession', true)
    }
  }, [])

  return (
    <SetupContextProvider>
      <InformationContextProvider>
        <Routes>
          {/* todo : remove setup api call and profile api calls in these routes, decide to which routes yet */}

          {/* Clinician join flow — clean URLs with SEO-friendly per-role slugs.
              Legacy /mentors/join?type=X and /doctors/join URLs are redirected
              to the new /join namespace so inbound links keep working. */}
          <Route path="/join" name="Clinician Join" element={<JoinPage view="index" />} />
          <Route
            path="/join/:slug"
            name="Clinician Join by Role"
            element={<JoinPage view="role" />}
          />

          {/* Legacy: /mentors/join?type=doctor → /join/doctor, etc. */}
          <Route
            exact
            path="/mentors/join"
            name="Legacy Mentor Join Redirect"
            element={<JoinPage view="legacy" />}
          />
          {/* Legacy: /doctors/join → /join/doctor */}
          <Route
            exact
            path="/doctors/join"
            name="Legacy Doctor Join Redirect"
            element={<Navigate to="/join/doctor" replace />}
          />
          <Route
            path="/mentors/apply"
            name="Mentors Join"
            element={
              <ProtectedRoute>
                <MentorPublicPage blankPage={true} />
              </ProtectedRoute>
            }
          />

          {/* workplace joining flow: */}
          <Route
            path="/aumhum-for-workplace"
            name="Work Landing Page"
            element={<BusinessPublicPage />}
          />
          <Route
            path="/aumhum-for-workplace/apply"
            name="Work Landing Page"
            element={
              <ProtectedRoute>
                <BusinessPublicPage blankPage={true} />
              </ProtectedRoute>
            }
          />

          {/* clinic joining flow: */}
          <Route
            path="/aumhum-for-clinics"
            name="Clinic Landing Page"
            element={<ClinicPromotionPublicPage />}
          />
          <Route
            path="/aumhum-for-clinics/apply"
            name="Clinic Landing Page"
            element={
              <ProtectedRoute>
                <ClinicPromotionPublicPage blankPage={true} />
              </ProtectedRoute>
            }
          />
          {/* events booking, listing pages: */}
          <Route
            path="/mentor/:mentorSlug/events/:eventId"
            name="Event Booking Page"
            element={<EventBooking />}
          />
          <Route
            path="/mentor/:mentorSlug/packs/:packId"
            name="Pack buying Page"
            element={<PackBuying />}
          />
          <Route
            path="/webview/mentor/:mentorSlug/events/:eventId"
            name="Event Booking Page"
            element={<EventBookingWrapperForWebview />}
          />
          <Route
            path="/mentor/:mentorSlug/events"
            name="Event Listing Page"
            element={<EventListing />}
          />

          {/* events joining pages: */}
          <Route
            path="/webview/l/:bookedEventId"
            name="Live Session Page"
            element={<LiveSessionPageWrapperForWebview withSlug={true} />}
          />
          <Route
            path="/l/:bookedEventId"
            name="Live Session Page"
            element={<LiveSessionScreen withSlug={true} />}
          />
          <Route element={<MyDedicatedPrescriptionPage />} path={`session/:eventId/prescription`} />
          <Route element={<RxViewerPage />} path="/rx/:linkToken" />
          <Route element={<ReceiptViewerPage />} path="/receipt/:token" />
          <Route
            path="/webview/live/:bookedEventId"
            name="Live Session Page"
            element={<LiveSessionPageWrapperForWebview />}
          />
          <Route
            path="/live/:bookedEventId"
            name="Live Session Page"
            element={<LiveSessionScreen />}
          />
          <Route
            path="/webinar/:bookedEventId"
            name="Webinar Session Page"
            element={<LiveSessionScreen />}
          />

          {/* hotline things: */}
          {/* <Route
            path="/hotline-for-mentor/:clinicId/:satelliteId"
            name="Mentor Side Hotline"
            element={<HotlineChamberPage forMentor={true} />}
          />
          <Route
            path="/hotline-for-clinic-chamber/:clinicId/:satelliteId"
            name="Clinic Sode Hotline"
            element={<HotlineChamberPage forMentor={false} />}
          /> */}

          {/* clinic pages for consumers: */}
          <Route
            path="/clinic/:clinicBrandSlug/id/:clinicId/:mentorSlug/:eventId"
            name="Booking Page with Id"
            element={<ClinicPublicBookingPageOfMentor />}
          />
          <Route
            path="/clinic/:clinicBrandSlug/:clinicSlug/:mentorSlug/:eventId"
            name="Booking Page with Slug"
            element={<ClinicPublicBookingPageOfMentor />}
          />
          <Route
            path="/clinic/:clinicBrandSlug/id/:clinicId"
            name="Clinic Location Marketing Page with Id"
            element={<ClinicLandingPage />}
          />
          <Route
            path="/clinic/:clinicBrandSlug/:clinicSlug"
            name="Clinic Location Marketing Page with Slug"
            element={<ClinicLandingPage showDoctors={true} showTherapists={true} />}
          />
          <Route
            path="/clinic/:clinicBrandSlug/:clinicSlug/doctors"
            name="Clinic Location Marketing Page with Slug"
            element={<ClinicLandingPage showDoctors={true} showTherapists={false} />}
          />
          <Route
            path="/clinic/:clinicBrandSlug/:clinicSlug/therapists"
            name="Clinic Location Marketing Page with Slug"
            element={<ClinicLandingPage showDoctors={false} showTherapists={true} />}
          />
          <Route
            path="/clinic/:clinicBrandSlug/:clinicSlug/therapy"
            name="Clinic Location Marketing Page with Slug"
            element={<ClinicLandingPage showDoctors={false} showTherapists={true} />}
          />
          <Route
            path="/clinic/:clinicBrandSlug"
            name="Clinic Brand Page with slug"
            element={<ClinicBrandLandingPage />}
          />

          {/* mentor page: */}
          <Route
            path="/mentor/:mentorSlug"
            name="Event Listing Page"
            element={<MentorLandingPage />}
          />

          {/* mentors listing pages: */}
          <Route
            path="/mentor"
            name="Mentors Listing"
            element={<TherapistPsychiatristListingPage />}
          />
          <Route
            path="/therapy-psychiatry"
            name="Therapists Listing"
            element={<TherapistPsychiatristListingPage />}
          />

          {/* Assessment Routes : */}
          <Route path="/assessment" name="Assessment Listing" element={<AssessmentListingPage />} />
          <Route path="/assessment/:slug" name="Assessment Player" element={<AssessmentPlayer />} />
          <Route
            path="/assessment/id/:assessmentId"
            name="Assessment Player"
            element={<AssessmentPlayer />}
          />
          <Route
            path="/assessments"
            name="Assessment Listing"
            element={<AssessmentListingPage />}
          />
          <Route
            path="/assessments/:slug"
            name="Assessment Player"
            element={<AssessmentPlayer />}
          />
          <Route
            path="/assessments/id/:assessmentId"
            name="Assessment Player"
            element={<AssessmentPlayer />}
          />

          {/* Article Routes */}
          <Route path="/article" name="Articles Listing" element={<ArticlesListingPage />} />
          <Route path="/article/:slug" name="Article Page" element={<ArticlePage />} />
          <Route path="/article/id/:articleId" name="Article Page" element={<ArticlePage />} />
          <Route path="/articles" name="Articles Listing" element={<ArticlesListingPage />} />
          <Route path="/articles/:slug" name="Article Page" element={<ArticlePage />} />
          <Route path="/articles/id/:articleId" name="Article Page" element={<ArticlePage />} />

          {/* Journal Routes */}
          <Route path="/journal" name="Journals Listing" element={<JournalsListingPage />} />
          <Route
            path="/journal/:slug"
            name="Journal Landing Page"
            element={<JournalLandingPage />}
          />
          <Route
            path="/journal/id/:journalId"
            name="Journal Landing Page"
            element={<JournalLandingPage />}
          />

          {/* Courses Routes */}
          <Route
            path="/c/:courseCategoryId/id/:courseId"
            name="Course Page"
            element={<CourseCategoryLandingPage />}
          />
          <Route
            path="/c/:courseCategoryId/:courseSlug"
            name="Course Page"
            element={<CourseCategoryLandingPage />}
          />
          <Route
            path="/podcast/:courseSlug"
            name="Podcast Page"
            element={<CourseCategoryLandingPage prechosenCategory={'podcast'} />}
          />
          <Route
            path="/c/:categorySlug"
            name="Course Listing Page Page"
            element={<CourseCategoryListingPage />}
          />
          <Route
            path="/podcast"
            name="Podcast Listing Page"
            element={<CourseCategoryListingPage prechosenCategory={'podcast'} />}
          />

          {/* Breathe Routes */}
          <Route path="/breathe" name="Breathe Listing" element={<BreatheListingPage />} />
          <Route path="/breathe/:slug" name="Breathe Player" element={<div />} />

          {/* Technique Routes */}
          <Route path="/technique" name="Technique Listing" element={<TechniqueListingPage />} />
          <Route path="/technique/id/:id" name="Technique Page" element={<TechniquePage />} />
          <Route path="/technique/:slug" name="Technique Page" element={<TechniquePage />} />

          {/* Timer Routes */}
          <Route path="/timer/i/:id" name="Timer Page" element={<TimerPage type="id" />} />
          <Route path="/timer/:id" name="Timer Page" element={<TimerPage type="slug" />} />
          {/* Quote Routes */}
          <Route path="/quotes/:slug" name="Quote Page" element={<div />} />

          {/* Activity Plan Routes */}
          <Route element={<UserActivityListingPage />} path="/my-activity-plans" />
          <Route element={<ActivityPlanListingPage />} path="/plan" />
          <Route element={<ActivityPlanPage />} path="/plan/:planId" />
          <Route element={<ActivityPlanPage useId />} path="/plan/id/:planId" />

          {/* Personalized Plan */}
          <Route
            element={<BehavioralAnxietyPlan />}
            path="/personalized-plan/behavioral-addiction-plan"
          />
          <Route
            path="/personalized-plan/behavioral-addiction-plan/addiction-selection"
            element={<AddictionTypesSelectionPage />}
          />
          <Route
            path="/personalized-plan/behavioral-addiction-plan/assessment-selection"
            element={<AddictionTypesSelectionPage type="assessment-selection" />}
          />

          {/* Others */}
          <Route
            path="/webinars"
            name="Webinars Listing"
            element={<WebinarListingPage header="public" />}
          />
          <Route path="/webinars/:slug" name="Webinar Page" element={<div />} />

          {/* infinite rerendering idk safety hack */}
          <Route
            path="/booking-requests-for-mentors/active"
            name="Booking requests page"
            element={<MentorBookingRequestActivePage />}
          />
          <Route
            path="/booking-requests-for-mentors/all"
            name="Booking requests page all"
            element={<MentorBookingRequestAllPage />}
          />

          {/* AUMHUM public marketing site — deep pages (these win over the
              /* catch-all below because React Router prefers specific matches). */}
          <Route path="/how-care-works" element={<AumhumSite view="how-care-works" />} />
          {/* /book-consultation forks the visitor into either the auth modal
              (returning patient) or /care/get-started?intent=consultation
              (new prospect). Replaces the old "Book consultation" → directory
              dead-end on /therapy-psychiatry. */}
          <Route path="/book-consultation" element={<AumhumSite view="book-consultation" />} />
          {/* /signup is the dedicated full-page account-creation flow.
              The TopBar Login modal is login-only; "New to AUMHUM? Create
              an account" navigates here. /welcome is the post-signup
              orchestration that picks where the new account goes (care
              prospect / wellness app / clinician). */}
          <Route path="/signup" element={<AumhumSite view="signup" />} />
          <Route path="/welcome" element={<AumhumSite view="welcome" />} />
          <Route path="/care/start" element={<AumhumSite view="care-start" />} />
          <Route path="/care/get-started" element={<AumhumSite view="care-get-started" />} />
          <Route path="/care/status" element={<AumhumSite view="care-status" />} />
          <Route path="/students" element={<AumhumSite view="students" />} />
          <Route path="/doctors/:slug" element={<AumhumSite view="doctor" />} />
          <Route path="/care-givers/:slug" element={<AumhumSite view="caregiver" />} />
          <Route path="/conditions/:slug" element={<AumhumSite view="condition" />} />

          {/* Moved: the previous public home is preserved at /self-care so existing
              links and campaigns that expect the self-care landing keep working. */}
          <Route path="/self-care" element={<SelfCarePublicLanding />} />

          {/* Catch-all: logged-out visitors see the new marketing site,
              authenticated users continue to get the tile experience via
              AppHomepage (which owns its own nested routes for /profile,
              /my-plan, /leaderboard, etc.). */}
          <Route path="/*" name="Application Home" element={<RootLanding />} />
        </Routes>
      </InformationContextProvider>
    </SetupContextProvider>
  )
}
